import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Send,
  Camera,
  Bot,
  User,
  Sparkles,
  Image,
  X,
  Loader2
} from "lucide-react";
import { toast } from "sonner";

const HauoraAI = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [imageMode, setImageMode] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [analyzingImage, setAnalyzingImage] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Initial greeting
    setMessages([{
      role: "ai",
      content: `Kia ora ${user?.name?.split(' ')[0]}! I'm Hauora AI, your wellness companion. I'm here to help you with:\n\n• Nutrition questions and NZ food info\n• Understanding calories and macros\n• Healthy meal ideas and food swaps\n• Setting goals using Te Whare Tapa Whā\n• Reflection and journaling prompts\n\nYou can also upload a photo of your meal and I'll estimate the nutritional content. How can I support your hauora journey today?`
    }]);
  }, [user]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() && !imageBase64) return;
    
    const userMessage = {
      role: "user",
      content: input,
      image: imagePreview
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      if (imageBase64) {
        // Image analysis
        setAnalyzingImage(true);
        const res = await axios.post(`${API}/ai/analyze-food`, {
          imageBase64: imageBase64,
          question: input || "Analyze this food image and estimate the nutritional content."
        });
        
        let aiResponse = "";
        if (res.data.analysis) {
          const a = res.data.analysis;
          aiResponse = `I can see the following foods:\n\n`;
          if (a.foods) {
            aiResponse += `**Foods:** ${a.foods.join(', ')}\n`;
          }
          if (a.portions) {
            aiResponse += `**Portions:** ${a.portions.join(', ')}\n`;
          }
          if (a.totals) {
            aiResponse += `\n**Estimated Nutrition:**\n`;
            aiResponse += `• Calories: ${a.totals.calories}\n`;
            aiResponse += `• Protein: ${a.totals.protein}g\n`;
            aiResponse += `• Carbs: ${a.totals.carbs}g\n`;
            aiResponse += `• Fats: ${a.totals.fats}g\n`;
          }
          if (a.notes) {
            aiResponse += `\n${a.notes}`;
          }
          aiResponse += `\n\n*Remember to log this in your Daily Journal!*`;
        } else {
          aiResponse = res.data.raw || "I had trouble analyzing that image. Could you try with a clearer photo?";
        }
        
        setMessages(prev => [...prev, { role: "ai", content: aiResponse }]);
        setImagePreview(null);
        setImageBase64(null);
        setImageMode(false);
      } else {
        // Text chat
        const res = await axios.post(`${API}/ai/chat`, {
          message: input,
          sessionId: sessionId
        });
        
        setSessionId(res.data.sessionId);
        setMessages(prev => [...prev, { role: "ai", content: res.data.response }]);
      }
    } catch (err) {
      console.error("AI error:", err);
      toast.error("Failed to get response");
      setMessages(prev => [...prev, { 
        role: "ai", 
        content: "Sorry, I'm having trouble connecting right now. Please try again." 
      }]);
    } finally {
      setLoading(false);
      setAnalyzingImage(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error("Please select a JPEG, PNG, or WEBP image");
      return;
    }
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      setImageBase64(base64);
      setImagePreview(reader.result);
      setImageMode(true);
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImagePreview(null);
    setImageBase64(null);
    setImageMode(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatMessage = (content) => {
    // Simple markdown-like formatting
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/•/g, '&#8226;')
      .split('\n')
      .map((line, i) => <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: line || '&nbsp;' }} />);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-100px)]" data-testid="hauora-ai">
      {/* Header */}
      <div className="p-4 border-b border-[#0B3D2E]/10 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#0B3D2E] flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#0B3D2E]">Hauora AI</h1>
            <p className="text-xs text-[#4A5D56]">Your wellness companion</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4 max-w-2xl mx-auto">
          {messages.map((msg, i) => (
            <div 
              key={i}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                  msg.role === 'user' ? 'bg-[#0B3D2E]' : 'bg-[#E3EBE5]'
                }`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-white" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-[#0B3D2E]" />
                  )}
                </div>
                <div className={`rounded-2xl p-3 ${
                  msg.role === 'user' 
                    ? 'chat-bubble-user' 
                    : 'chat-bubble-ai'
                }`}>
                  {msg.image && (
                    <img 
                      src={msg.image} 
                      alt="Uploaded" 
                      className="max-w-[200px] rounded-lg mb-2"
                    />
                  )}
                  <div className="text-sm leading-relaxed">
                    {formatMessage(msg.content)}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="flex gap-2">
                <div className="w-8 h-8 rounded-full bg-[#E3EBE5] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#0B3D2E]" />
                </div>
                <div className="chat-bubble-ai rounded-2xl p-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">
                      {analyzingImage ? "Analyzing your meal..." : "Thinking..."}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Image Preview */}
      {imagePreview && (
        <div className="p-2 border-t border-[#0B3D2E]/10 bg-[#F9F9F7]">
          <div className="relative inline-block">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="h-20 rounded-lg"
            />
            <button
              onClick={clearImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-[#4A5D56] mt-1">
            Add a message or send to analyze
          </p>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-[#0B3D2E]/10 bg-white">
        <div className="flex gap-2 max-w-2xl mx-auto">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
          />
          <Button
            variant="outline"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="flex-shrink-0 border-[#0B3D2E]/20"
            data-testid="upload-image-btn"
          >
            <Camera className="w-5 h-5 text-[#0B3D2E]" />
          </Button>
          <Input
            placeholder={imageMode ? "Describe your meal (optional)..." : "Ask me anything..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !loading && sendMessage()}
            className="tribe-input"
            data-testid="chat-input"
          />
          <Button
            onClick={sendMessage}
            disabled={loading || (!input.trim() && !imageBase64)}
            className="btn-primary px-4"
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-center text-[10px] text-[#8FA39B] mt-2">
          Not medical advice. Talk to your Coach or healthcare provider for personalized guidance.
        </p>
      </div>
    </div>
  );
};

export default HauoraAI;
